import L from 'leaflet'
import { message, notification } from 'ant-design-vue'
import { MapDoodleEnum } from '/@/types/map-enum'
import { getRoot } from '/@/root'
import { PostFlightAreaBody, saveFlightArea } from '/@/api/flight-area'
import { generateCircleContent, generatePolyContent } from '/@/utils/map-layer-utils'
import { GeojsonCoordinate } from '/@/utils/genjson'
// import { gcj02towgs84, wgs84togcj02 } from '/@/vendors/coordtransform.js'
import { uuidv4 } from '/@/utils/uuid'
import { CommonHostWs } from '/@/websocket'
import { FlightAreasDroneLocation, EFlightAreaType, EFlightAreaUpdate, FlightAreaUpdate } from '/@/types/flight-area'
import EventBus from '/@/event-bus/'
import rootStore from '/@/store'
import { h } from 'vue'
import { useGMapCover } from '/@/hooks/use-g-map-cover'
import moment from 'moment'
import { DATE_FORMAT } from '/@/utils/constants'

export function useFlightArea () {
  const root = getRoot()
  const store = rootStore
  const coverMap = store.state.coverMap

  const useGMapCoverHook = useGMapCover()

  const MIN_RADIUS = 10

  function checkCircle (layer: L.Circle): boolean {
    if (layer.getRadius() < MIN_RADIUS) {
      message.error(`The radius must be greater than ${MIN_RADIUS}m.`)
      root.$map.removeLayer(layer)
      return false
    }
    return true
  }

  function checkPolygon (layer: L.Polygon): boolean {
    const latlngs = layer.getLatLngs() as L.LatLng[][]
    if (!latlngs || latlngs[0].length < 3) {
      message.error('The path of the polygon cannot be crossed.')
      root.$map.removeLayer(layer)
      return false
    }
    return true
  }

  function buildExtData (type: string) {
    const id = uuidv4()
    const name = `${type}-${moment().format(DATE_FORMAT)}`
    return { id, name, type }
  }

  function latlngToCoord (ll: L.LatLng): GeojsonCoordinate {
    return [ll.lng, ll.lat]
  }

  function handleCreateFlightArea (layer: any, mapType: string, ext: { id: string, type: EFlightAreaType, name: string }, groupId?: string) {
    if (!ext || !ext.id) {
      console.warn('handleCreateFlightArea: ext is missing or invalid, generating new one.')
      const type = (layer as any)._extData?.type || EFlightAreaType.DFENCE
      ext = buildExtData(type) as any
    }
    const data = { id: ext.id, type: ext.type, name: ext.name, groupId }
    let coordinates: GeojsonCoordinate | GeojsonCoordinate[][]
    let content: any

    if (mapType === 'circle') {
      const center = (layer as L.Circle).getLatLng()
      const radius = (layer as L.Circle).getRadius()
      const feature = generateCircleContent({ lng: center.lng, lat: center.lat } as any, radius) as any
      content = {
        properties: feature.content.properties,
        geometry: feature.content.geometry
      }
      coordinates = getWgs84(content.geometry.coordinates as GeojsonCoordinate)
    } else if (mapType === 'polygon') {
      const latlngs = (layer as L.Polygon).getLatLngs() as L.LatLng[][]
      const coords = latlngs[0].map(ll => latlngToCoord(ll))
      content = generatePolyContent(coords.map(c => ({ lng: c[0] as number, lat: c[1] as number } as any))).content
      coordinates = [getWgs84(content.geometry.coordinates[0] as GeojsonCoordinate[])]
    } else {
      message.error(`Invalid type: ${mapType}`)
      root.$map.removeLayer(layer)
      return
    }

    content.geometry.coordinates = coordinates
    const finalBody = Object.assign({}, data, { content })
    console.log('Sending saveFlightArea body:', finalBody)

    saveFlightArea(finalBody as PostFlightAreaBody).then(res => {
      console.log('saveFlightArea response:', res)
      if (res.code === 0) {
        const updatePayload: FlightAreaUpdate = {
          operation: EFlightAreaUpdate.ADD,
          area_id: (finalBody as any).id,
          name: (finalBody as any).name,
          type: (finalBody as any).type,
          content: (finalBody as any).content,
          status: true,
          username: localStorage.getItem('username') || '',
          create_time: Date.now(),
          update_time: Date.now()
        }
        EventBus.emit('flightAreasUpdateWs', updatePayload)
      } else {
        useGMapCoverHook.removeCoverFromMap(ext.id)
      }
    }).finally(() => root.$map.removeLayer(layer))
  }

  /**
   * @deprecated Use handleCreateFlightArea directly from getDrawCallback in GMap.vue
   */
  function getDrawFlightAreaCallback (type: MapDoodleEnum) {
    console.warn('getDrawFlightAreaCallback is deprecated. Use handleCreateFlightArea instead.')
  }

  const getWgs84 = <T extends any>(coordinate: T): T => {
    // OSM 사용으로 인해 변환 불필요
    return coordinate
  }

  const getGcj02 = <T extends any>(coordinate: T): T => {
    // OSM 사용으로 인해 변환 불필요
    return coordinate
  }

  const onFlightAreaDroneLocationWs = (data: CommonHostWs<FlightAreasDroneLocation>) => {
    const nearArea = data.host.drone_locations.filter(val => !val.is_in_area)
    const inArea = data.host.drone_locations.filter(val => val.is_in_area)
    notification.warning({
      key: `flight-area-${data.sn}`,
      message: `Drone(${data.sn}) flight area information`,
      description: h('div',
        [
          h('div', [
            h('span', { class: 'fz18' }, 'In the flight area: '),
            h('ul', [
              ...inArea.map(val => h('li', `There are ${val.area_distance} meters from the edge of the area(${coverMap[val.area_id]?.[1] || val.area_id}).`))
            ])
          ]),
          h('div', [
            h('span', { class: 'fz18' }, 'Near the flight area: '),
            h('ul', [
              ...nearArea.map(val => h('li', `There are ${val.area_distance} meters from the edge of the area(${coverMap[val.area_id]?.[1] || val.area_id}).`))
            ])
          ])
        ]),
      duration: null,
      style: {
        width: '420px',
        marginTop: '-8px',
        marginLeft: '-28px',
      }
    })
  }

  return {
    getDrawFlightAreaCallback,
    handleCreateFlightArea,
    getGcj02,
    getWgs84,
    onFlightAreaDroneLocationWs,
    buildExtData,
  }
}
